import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaMicrochip,
    FaPlus,
    FaTrash,
    FaCheckCircle,
    FaRegCircle,
    FaExclamationCircle,
    FaArrowUp
} from "react-icons/fa";
import { setActiveSerial as saveActiveSerial } from "../services/activeDevice";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import AddDeviceModal from "../components/AddDeviceModal";
import { listDevices, deleteDevice } from "../services/devices";
import { getLatestFirmware } from "../services/firmware";

import "../styles/devices.css";

const ACTIVE_KEY = "activeDeviceSerial";

export default function Devices() {

    const navigate = useNavigate();

    const [menuOpen, setMenuOpen] = useState(false);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    // Distinct from "devices.length === 0" - this means the request
    // itself failed, so we don't actually know how many devices exist.
    const [loadError, setLoadError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [activeSerial, setActiveSerial] = useState(
        () => localStorage.getItem(ACTIVE_KEY) || ""
    );
    const [deletingSerial, setDeletingSerial] = useState(null);
    const [latestFirmware, setLatestFirmware] = useState(null);

    useEffect(() => {

        loadDevices();

        getLatestFirmware()
            .then(res => setLatestFirmware(res.data.latest))
            .catch(() => setLatestFirmware(null)); // non-critical - just skip the badge if this fails

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadDevices() {

        setLoading(true);
        setLoadError(null);

        try {

            const res = await listDevices();
            const list = res.data.devices || [];

            setDevices(list);

            // Make sure there's always a sensible active device
            const stillExists = list.some(d => d.serial_no === activeSerial);

            if (!stillExists && list.length > 0) {
                setActive(list[0].serial_no);
            } else if (list.length === 0) {
                localStorage.removeItem(ACTIVE_KEY);
                setActiveSerial("");
            }

        } catch (err) {

            console.error("Failed to load devices:", err);

            if (err.response?.status === 401) {

                setLoadError(
                    "Your session has expired. Please log in again."
                );

            } else {

                setLoadError(
                    `Couldn't load your devices` +
                    (err.response?.status ? ` (error ${err.response.status})` : "") +
                    `. Check your connection and try again.`
                );

            }

        } finally {

            setLoading(false);

        }

    }

    function setActive(serial) {

    saveActiveSerial(serial);

    setActiveSerial(serial);

}

    async function handleAdded(device) {

        const wasEmpty = devices.length === 0;

        setShowModal(false);

        // Re-fetch from the server rather than trusting the response
        // we just got - this is the source of truth, and it's what
        // catches cases where the insert didn't actually persist.
        await loadDevices();

        // First device added becomes the active one automatically
        if (wasEmpty) {
            setActive(device.serial);
        }

    }

    async function handleDelete(serial) {

        if (!window.confirm(`Remove device ${serial}? This can't be undone.`))
            return;

        setDeletingSerial(serial);

        try {

            await deleteDevice(serial);

            setDevices(prev => prev.filter(d => d.serial_no !== serial));

            if (activeSerial === serial) {
                localStorage.removeItem(ACTIVE_KEY);
                setActiveSerial("");
            }

        } catch (err) {

            alert(
                err.response?.data?.message ||
                "Couldn't remove that device. Please try again."
            );

        } finally {

            setDeletingSerial(null);

        }

    }

    return (

        <div className="layout">

            <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

            <div className="content">

                <Topbar
                    setMenuOpen={setMenuOpen}
                    title="My Devices"
                    subtitle={`${devices.length} device${devices.length === 1 ? "" : "s"} registered`}
                />

                <div className="devicesBody">

                    <button
                        className="addDeviceBtn"
                        onClick={() => setShowModal(true)}
                    >
                        <FaPlus /> Add Device
                    </button>

                    {loading ? (

                        <div className="devicesEmpty">Loading your devices...</div>

                    ) : loadError ? (

                        <div className="devicesEmpty devicesError">
                            <FaExclamationCircle />
                            <h3>Couldn't load your devices</h3>
                            <p>{loadError}</p>

                            {loadError.includes("session has expired") ? (
                                <button
                                    className="devicesRetryBtn"
                                    onClick={() => {
                                        localStorage.clear();
                                        navigate("/login");
                                    }}
                                >
                                    Log in again
                                </button>
                            ) : (
                                <button
                                    className="devicesRetryBtn"
                                    onClick={loadDevices}
                                >
                                    Retry
                                </button>
                            )}
                        </div>

                    ) : devices.length === 0 ? (

                        <div className="devicesEmpty">
                            <FaMicrochip />
                            <h3>No devices yet</h3>
                            <p>Add your inverter's Device ID to start monitoring it live.</p>
                        </div>

                    ) : (

                        <div className="deviceCards">

                            {devices.map(d => (

                                <div
                                    className={
                                        d.serial_no === activeSerial
                                            ? "deviceCardRow activeDevice"
                                            : "deviceCardRow"
                                    }
                                    key={d.serial_no}
                                >

                                    <button
                                        className="deviceActiveToggle"
                                        onClick={() => setActive(d.serial_no)}
                                        title={
                                            d.serial_no === activeSerial
                                                ? "Shown on Dashboard"
                                                : "Show on Dashboard"
                                        }
                                    >
                                        {d.serial_no === activeSerial
                                            ? <FaCheckCircle />
                                            : <FaRegCircle />
                                        }
                                    </button>

                                    <div className="deviceCardIcon">
                                        <FaMicrochip />
                                    </div>

                                    <div className="deviceCardInfo">
                                        <h4>{d.nickname || d.serial_no}</h4>
                                        <p>{d.serial_no}</p>
                                        <span>{d.brand} &middot; {d.model}</span>

                                        <div className="deviceFirmwareRow">

                                            <span className="deviceFirmwareVersion">
                                                Firmware: {d.firmware_version || "Unknown"}
                                            </span>

                                            {latestFirmware && d.firmware_version && (

                                                d.firmware_version === latestFirmware.version ? (

                                                    <span className="firmwareBadge upToDate">
                                                        <FaCheckCircle /> Up to date
                                                    </span>

                                                ) : (

                                                    <span className="firmwareBadge updateAvailable">
                                                        <FaArrowUp /> Update available ({latestFirmware.version})
                                                    </span>

                                                )

                                            )}

                                        </div>

                                    </div>

                                    <button
                                        className="deviceDeleteBtn"
                                        onClick={() => handleDelete(d.serial_no)}
                                        disabled={deletingSerial === d.serial_no}
                                        aria-label={`Remove ${d.serial_no}`}
                                    >
                                        <FaTrash />
                                    </button>

                                </div>

                            ))}

                        </div>

                    )}

                </div>

            </div>

            {showModal && (
                <AddDeviceModal
                    onClose={() => setShowModal(false)}
                    onAdded={handleAdded}
                />
            )}

        </div>

    );

}
